import { clamp } from 'lodash';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';

interface Props {
  children: [ReactNode, ReactNode];
  minWidth?: number; // in pixels
  className?: string;
  onDimsChanged: () => void;
}

export const MIN_WIDTH = 550;
export const MAX_WIDTH = 900;
export const RESIZE_STEP = 50;
export const KEY_RESIZE_DEBOUNCE_MS = 200;
export const DRAG_THRESHOLD_PX = 3;

type ResizeMethod = 'drag' | 'keyboard' | 'click';

function trackResize(method: ResizeMethod, width: number) {
  capturePosthogEvent(POSTHOG_EVENTS.projectDetailsPanelResized, {
    method,
    panel_width: width,
    page_path: window.location.pathname,
  });
}

/** Widths a click on the handle steps through: narrowest, midpoint, widest. */
export function presetWidths(minWidth: number) {
  return [minWidth, Math.round((minWidth + MAX_WIDTH) / 2), MAX_WIDTH];
}

export default function Splitter({ minWidth = MIN_WIDTH, onDimsChanged, className = '', children }: Props) {
  const pane1 = children[0];
  const pane2 = children[1];

  const panelId = useId();
  const pointerDrag = useRef<{ startX: number; startWidth: number; moved: boolean } | null>(null);
  const panelWidthRef = useRef(0);
  const suppressClick = useRef(false);
  const pendingKeyWidth = useRef<number | null>(null);
  const keyResizeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [announcedWidth, setAnnouncedWidth] = useState<number | null>(null);
  const [sliderPosition, setSliderPosition] = useState<{
    isDragging: boolean;
    startWidth: number;
    start: { x: number; y: number };
    current: { x: number; y: number };
  }>({
    isDragging: false,
    startWidth: 0,
    start: { x: 0, y: 0 },
    current: { x: 0, y: 0 },
  });

  const panelWidth = useMemo(() => {
    const baseWidth = sliderPosition.startWidth || minWidth;
    const width = baseWidth + (sliderPosition.start.x - sliderPosition.current.x);

    return clamp(width, minWidth, MAX_WIDTH);
  }, [minWidth, sliderPosition]);
  panelWidthRef.current = panelWidth;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    onDimsChanged();
  }, [panelWidth]);

  useEffect(() => () => clearTimeout(keyResizeTimer.current), []);

  const cancelPendingKeyResize = () => {
    clearTimeout(keyResizeTimer.current);
    pendingKeyWidth.current = null;
    setAnnouncedWidth(null);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    cancelPendingKeyResize();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointerDrag.current = { startX: e.screenX, startWidth: panelWidth, moved: false };

    setSliderPosition({
      isDragging: true,
      startWidth: e.currentTarget.parentElement?.getBoundingClientRect().width ?? panelWidth,
      start: {
        x: e.screenX,
        y: e.screenY,
      },
      current: {
        x: e.screenX,
        y: e.screenY,
      },
    });
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = pointerDrag.current;
    if (!drag) return;
    if (Math.abs(e.screenX - drag.startX) >= DRAG_THRESHOLD_PX) drag.moved = true;
    if (!drag.moved) return;

    setSliderPosition((sliderPosition) => ({
      ...sliderPosition,
      current: {
        x: e.screenX,
        y: e.screenY,
      },
    }));
  };

  const endPointerDrag = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!pointerDrag.current) return;
    // The click that follows a drag's release is not a click on the handle.
    if (e.type === 'pointerup' && pointerDrag.current.moved) {
      suppressClick.current = true;
      setTimeout(() => {
        suppressClick.current = false;
      }, 0);
      if (panelWidthRef.current !== pointerDrag.current.startWidth) trackResize('drag', panelWidthRef.current);
    }
    pointerDrag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);

    setSliderPosition((sliderPosition) => ({
      ...sliderPosition,
      isDragging: false,
    }));
  };

  const setWidth = (width: number) => {
    setSliderPosition((sliderPosition) => ({
      ...sliderPosition,
      startWidth: width,
      start: { x: 0, y: 0 },
      current: { x: 0, y: 0 },
    }));
  };

  // A click, tap, Enter or Space steps to the next preset width: the single-pointer alternative to dragging.
  const onClick = () => {
    if (suppressClick.current) return;
    const fromWidth = pendingKeyWidth.current ?? panelWidth;
    cancelPendingKeyResize();
    const nextWidth = presetWidths(minWidth).find((width) => width > fromWidth) ?? minWidth;
    setWidth(nextWidth);
    if (nextWidth !== panelWidth) trackResize('click', nextWidth);
  };

  // The handle sits on the panel's left edge, so ArrowLeft widens the panel.
  // Rapid presses accumulate and resize once, since every width change re-lays out the map.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.nativeEvent.isComposing) return;
    const fromWidth = pendingKeyWidth.current ?? panelWidth;
    const targetWidths: Record<string, number> = {
      ArrowLeft: fromWidth + RESIZE_STEP,
      ArrowRight: fromWidth - RESIZE_STEP,
      Home: minWidth,
      End: MAX_WIDTH,
    };
    if (!(e.key in targetWidths)) return;
    e.preventDefault();

    const nextWidth = clamp(targetWidths[e.key], minWidth, MAX_WIDTH);
    pendingKeyWidth.current = nextWidth;
    setAnnouncedWidth(nextWidth);

    clearTimeout(keyResizeTimer.current);
    keyResizeTimer.current = setTimeout(() => {
      pendingKeyWidth.current = null;
      setAnnouncedWidth(null);
      if (nextWidth !== panelWidthRef.current) trackResize('keyboard', nextWidth);
      setWidth(nextWidth);
    }, KEY_RESIZE_DEBOUNCE_MS);
  };

  const valueNow = announcedWidth ?? panelWidth;

  return (
    <div className={`flex ${sliderPosition.isDragging ? 'select-none' : ''} ${className}`}>
      <div className="flex grow">{pane1}</div>
      <div
        id={panelId}
        className="flex flex-row overflow-auto border-l border-grey-30"
        style={{ width: panelWidth }}
        data-testid="splitter-panel"
      >
        {/* biome-ignore lint/a11y/useSemanticElements: an <hr> can't take focus or keyboard input, which a resizable window splitter needs */}
        <button
          type="button"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          aria-controls={panelId}
          aria-valuenow={valueNow}
          aria-valuemin={minWidth}
          aria-valuemax={MAX_WIDTH}
          aria-valuetext={`${valueNow} pixels wide`}
          className="group fixed outline-hidden flex h-full min-w-6 -translate-x-2 cursor-ew-resize touch-none items-center justify-center"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointerDrag}
          onPointerCancel={endPointerDrag}
          onClick={onClick}
          onKeyDown={onKeyDown}
          data-testid="splitter-handle"
        >
          <span
            className="h-48 min-w-[4px] rounded-full bg-grey-50 group-hover:min-w-[10px] group-hover:bg-secondary group-focus-visible:min-w-[10px] group-focus-visible:bg-secondary group-focus-visible:ring-2 group-focus-visible:ring-focus group-focus-visible:ring-offset-1 group-focus-visible:ring-offset-white"
            data-testid="splitter-handle-grip"
          ></span>
        </button>
        <div className="m-4 flex min-w-0 grow flex-row bg-white">{pane2}</div>
      </div>
    </div>
  );
}
